import React from 'react';
import styled from 'styled-components';
import { Meeting } from 'api/meeting/types';
import { TimesheetCalendarSourceType } from 'api/timesheets';
import { Loading } from 'components/atoms/loading';
import { useGetMeetingSettings } from 'queries/meetings';
import { useTimesheetIntegrationsQuery } from 'queries/timesheets/calendar-integration-provider';
import { AiFillMeta } from '../types';
import { getCalendarSourceTypeFromAiFillSettings } from '../utils';
import { ReAuthCalendar } from './calendar-integrations/re-auth';
import { SetupCalendar } from './calendar-integrations/select-calendar';
import {
  MeetingSelectorUnrestricted,
  MEETING_CONTAINER_HEIGHT,
} from './meeting-selector-content';

interface Props {
  onGenerate: (meetings: Meeting[]) => void;
  meta?: AiFillMeta;
  classNameForWrapper?: string;
}

const Wrapper = styled.div`
  width: 400px;
  height: ${MEETING_CONTAINER_HEIGHT}px;
`;

const LoadingWrapper = styled.div`
  width: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${MEETING_CONTAINER_HEIGHT}px;
`;

const InternalMeetingSelector = ({
  onGenerate,
  classNameForWrapper,
  meta: aiFillMeta,
}: Props) => {
  const { data: aiFillSettings } = useGetMeetingSettings();

  // AI Fills and Timesheets have the same flow to get integration OAuth meta
  const { data: integrations, isLoading } = useTimesheetIntegrationsQuery(
    aiFillSettings?.calendar ?? TimesheetCalendarSourceType.GoogleCalendar,
    {
      enabled:
        aiFillSettings !== undefined &&
        aiFillSettings.aiFillsEnabled &&
        !aiFillSettings?.gongEnabled,
    },
  );

  const integration = integrations?.find(
    (meta) => meta.integrationStatus === 'ACTIVE',
  );
  const expiredIntegration = integrations?.find(
    (meta) => meta.integrationStatus === 'AUTHENTICATION_EXPIRED',
  );

  if (isLoading || aiFillSettings === undefined) {
    return (
      <LoadingWrapper>
        <Loading />
      </LoadingWrapper>
    );
  }

  // If Gong is enabled, no check show meetings, else show calendar connect if its not connected.
  const check = aiFillSettings?.gongEnabled ? false : !integration;

  return (
    <Wrapper className={classNameForWrapper}>
      {check ? (
        <>
          {expiredIntegration ? (
            <ReAuthCalendar
              sourceType={getCalendarSourceTypeFromAiFillSettings(
                aiFillSettings,
              )}
              expiredIntegration={expiredIntegration}
            />
          ) : (
            <SetupCalendar
              sourceType={getCalendarSourceTypeFromAiFillSettings(
                aiFillSettings,
              )}
            />
          )}
        </>
      ) : (
        <MeetingSelectorUnrestricted
          onGenerate={onGenerate}
          meta={aiFillMeta}
        />
      )}
    </Wrapper>
  );
};

/* Doing multi component in one file since downstream querykey is dependent on timesheetconfig */
/**
 *
 * @description MeetingSelector component is used to get the Meeting selector for AI Fills.
 */
export const MeetingSelector = ({
  onGenerate,
  classNameForWrapper,
  meta,
}: Props) => {
  const { isLoading: isSettingsLoading } = useGetMeetingSettings();

  return isSettingsLoading ? (
    <LoadingWrapper>
      <Loading />
    </LoadingWrapper>
  ) : (
    <InternalMeetingSelector
      meta={meta}
      onGenerate={onGenerate}
      classNameForWrapper={classNameForWrapper}
    />
  );
};
